import { Fragment, ReactElement, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { paths } from 'configuration/paths';
import { SurveyQuestion } from 'types/survey';
import { IMAGE_CATEGORY, VIDEO_CATEGORY } from 'types/attachmentTypes';
import { Attachment } from 'types/surveyResponse';
import { TranslationsKeys } from 'types/translation';
import { useTranslate } from 'hooks/useTranslate';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { useSurveyDefinitionQuery, useSurveyResponseDirectAPIQuery } from 'hooks/queries';
import { Images } from 'components/Images';
import { SurveyHeader } from 'components/SurveyHeader';
import { Footer } from 'components/Footer';
import { Button } from 'components/ui/Button';
import { getAttachmentsOfType, getQuestion, getSubsection, isVisible, sortSurvey } from 'utils/survey';
import styles from './ViewSurvey.module.scss';

const ViewSurvey = (): ReactElement => {
  const { surveyId } = useParams();
  const { t } = useTranslate();
  const surveyResponseQuery = useSurveyResponseDirectAPIQuery(surveyId);
  const surveyResponse = surveyResponseQuery.data;
  const surveyDefinitionQuery = useSurveyDefinitionQuery(
    surveyResponse ? surveyResponse.surveyDefinitionId : undefined
  );
  const surveyDefinition = useMemo(
    () => (surveyDefinitionQuery.data ? sortSurvey(surveyDefinitionQuery.data) : undefined),
    [surveyDefinitionQuery.data]
  );
  const ts = useSurveyTranslation(surveyDefinition);
  const isLoading =
    surveyResponseQuery.isLoading ||
    surveyDefinitionQuery.isLoading;
  const isError =
    surveyResponseQuery.isError ||
    surveyDefinitionQuery.isError;
  const navigate = useNavigate();
  
  const formatAnswer = (question: SurveyQuestion) => {
    const answer = getQuestion(surveyResponse!, question.questionId);
    if (!answer?.value) {
      return '—';
    }

    if (Array.isArray(answer.value)) {
      return answer.value.join(', ');
    }

    if (question.type === 'numeric' && question.input === 'units' && typeof answer.value === 'string' && answer.value.indexOf('|') !== -1) {
      const v = answer.value.split('|');
      return `${v[1]} ${v[0]}`;
    }

    return ts(answer.value.toString());
  };
  
  const outputImages = (attachments: Attachment[] | undefined) => {
    if (!attachments) {
      return null;
    }
    const images = getAttachmentsOfType(attachments, [IMAGE_CATEGORY, VIDEO_CATEGORY]);
    if (!images.length) {
      return null;
    }
    return <div className={styles.viewSurveyImages}>
      <Images images={images} />
    </div>;
  };

  return (
    <>
      <SurveyHeader
        isLoading={isLoading}
        isError={isError}
        surveyDefinition={surveyDefinition}
        surveyResponse={surveyResponse ?? undefined}
      />

      {surveyDefinition && surveyResponse &&
        <div className={styles.viewSurvey}>
          {surveyDefinition.sections.filter(s => isVisible(s, surveyResponse)).map(s => <div className={styles.viewSurveySection} key={s.sectionId}>
            <h2>{ts(s.name)}</h2>
            {s.subsections.filter(ss => isVisible(ss, surveyResponse)).map(ss => <Fragment key={ss.subsectionId}>
              <h3>{ts(ss.name)}</h3>
              {ss.questions.filter(q => isVisible(q, surveyResponse)).map(q => <p key={q.questionId}>
                {ts(q.name)}
                {!ts(q.name).endsWith(':') && !ts(q.name).endsWith('?') && ':'}
                {' '}
                {formatAnswer(q)}
                {outputImages(getQuestion(surveyResponse, q.questionId)?.attachments)}
              </p>)}
              {outputImages(getSubsection(surveyResponse, s.sectionId, ss.subsectionId)?.attachments)}
            </Fragment>)}
          </div>)}
      </div>}

      <Footer className={styles.viewSurveyFooter}>
        <div className={styles.viewSurveyFooterWrap}>
          <Button
            variant="secondary"
            onClick={() => navigate(paths.mySurveys_completed)}>
            {t(TranslationsKeys.common_close)}
          </Button>
        </div>
      </Footer>
    </>
  );
};

export default ViewSurvey;
